import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { captureMessage } from '@sentry/react';

import { PlatformAppUiSchema } from '@typings';
import { isSchemaField } from '@utils/guards';

import { Field, Render } from '@components/Shared';

import { PlatformAppsUiSchema } from './PlatformAppsUiSchema';

const PRIMITIVE_TYPES = ['number', 'integer', 'float', 'string'];

type Metadata = {
  title?: string;
  description?: string;
};

type Props = {
  /**
   * Top level property
   * Renders top level heading properly
   */
  isSchemaHeading?: boolean;
  schema: PlatformAppUiSchema.AnyOf;
};

export const PlatformAppUiSchemaAnyOf: React.FC<Props> = ({
  isSchemaHeading,
  schema,
  schema: { name, children: nodes, default: defaultValue },
}) => {
  const { register, unregister, setValue, clearErrors } = useFormContext();

  const defaultState = (() => {
    if (defaultValue === undefined) {
      return 0;
    }

    const nullIndex = nodes.findIndex(
      (node) => isSchemaField(node) && node.properties.type === 'null',
    );

    if (defaultValue === null || defaultValue === 'null') {
      return nullIndex;
    }

    return nullIndex === 0 ? 1 : 0;
  })();

  const [state, setState] = useState(defaultState);

  const currentNode = nodes[state];

  const extractNullishNode = (schema: PlatformAppUiSchema.Model) =>
    isSchemaField(schema) && schema.properties.type === 'null';

  /**
   * Helper to get is current state `"null"`
   */
  const isNullishState = !!extractNullishNode(currentNode);
  const isPrimitive =
    isSchemaField(currentNode) &&
    PRIMITIVE_TYPES.includes(currentNode.properties.type);
  const isBoolean =
    isSchemaField(currentNode) && currentNode.properties.type === 'boolean';
  /**
   * For case with two nodes with nullable node, render checkbox.
   * Otherwise, render select
   */
  const isCheckbox = nodes.length === 2 && nodes.some(extractNullishNode);

  useEffect(() => {
    /**
     * Track `anyOf` schemas wrapper,
     * to set `null` value on initialization
     */
    register(name);

    return () => {
      try {
        unregister(name);
      } catch {
        captureMessage(`Failed to unregister ${name}`);
      }
    };
  }, [name, register, unregister]);

  const removeFormData = () => {
    setValue(name, {});
  };

  /**
   * When state changes, transform form state to meet `anyOf` node schema.
   * Reset validation errors on state change
   */
  useEffect(() => {
    if (isNullishState) {
      setValue(name, null);
    } else if (isPrimitive) {
      setValue(name, '');
    } else if (isBoolean) {
      setValue(name, false);
    }

    return () => {
      clearErrors(name);
    };
  }, [isNullishState, isBoolean, isPrimitive, name, setValue, clearErrors]);

  const getNonNullishMetadata = (): Metadata => {
    const model = schema.children.find((schema) => {
      if (isSchemaField(schema)) {
        return schema.properties.type !== 'null';
      }

      return true;
    });

    if (schema.title) {
      return { title: schema.title, description: schema.description };
    }

    if (!model) {
      return { title: 'Non-Nullish' };
    }

    if (isSchemaField(model)) {
      return {
        title: model.properties.title,
        description: model.properties.description,
      };
    }

    return {
      title: model.title,
      description: model.description,
    };
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    removeFormData();

    const { checked } = event.target;

    const index = nodes.findIndex((schema) => {
      const isNonNullishSchema = !extractNullishNode(schema);

      return checked ? isNonNullishSchema : !isNonNullishSchema;
    });

    setState(index);
  };

  const makeOption = (
    { title, name }: PlatformAppUiSchema.Model,
    index: number,
  ) => {
    return (
      <option key={index} value={index}>
        {title ?? name}
      </option>
    );
  };

  const renderModel = () => {
    const node = nodes[state];

    if (isNullishState) {
      return null;
    }

    if (!node) {
      return <p>Not found node for (state)</p>;
    }

    const { title, description } = schema;

    return (
      <div className="ml-4">
        <Render if={isSchemaHeading}>
          <div className="mb-8 flex items-start gap-3">
            <div className="flex flex-col">
              <p className="text-h4">{title}</p>
              <Render if={description}>
                <p className="text-footnote text-neural-04">{description}</p>
              </Render>
            </div>
          </div>
        </Render>
        <div className="mt-0">
          <PlatformAppsUiSchema withFormatter={false} schema={node} />
        </div>
      </div>
    );
  };

  if (isCheckbox) {
    const { title, description } = getNonNullishMetadata();

    return (
      <div>
        <Field.Checkbox
          checked={!isNullishState}
          label={title}
          note={description}
          onChange={handleCheckboxChange}
          containerClassName="mb-4"
        >
          {title}
        </Field.Checkbox>
        {renderModel()}
      </div>
    );
  }

  return (
    <div>
      <Field.Select
        label={schema.title}
        note={schema.description}
        value={state}
        containerClassName="mb-4"
        onChange={(event) => {
          removeFormData();
          setState(Number(event.target.value));
        }}
      >
        {nodes.map(makeOption)}
      </Field.Select>
      {renderModel()}
    </div>
  );
};
